import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import '../../styles/Pages.css';
import '../../styles/Events.css';

const API = `${process.env.REACT_APP_API_URL}/api`;

// Get or create a persistent voterKey for this browser
function getVoterKey() {
    let key = localStorage.getItem('smcVoterKey');
    if (!key) {
        key = uuidv4();
        localStorage.setItem('smcVoterKey', key);
    }
    return key;
}

function formatCountdown(endTime) {
    const diff = new Date(endTime) - new Date();
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function PollVotePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const voterKey = getVoterKey();

    const [poll, setPoll] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [voteMsg, setVoteMsg] = useState('');
    const [alreadyVoted, setAlreadyVoted] = useState(false);
    const [votedOption, setVotedOption] = useState(null);
    const [countdown, setCountdown] = useState('');
    const [isClosed, setIsClosed] = useState(false);

    // Load poll
    useEffect(() => {
        window.scrollTo(0, 0);
        Promise.all([
            fetch(`${API}/polls/${id}`).then(r => r.json()),
            fetch(`${API}/polls/${id}/check-vote?voterKey=${voterKey}`).then(r => r.json()),
        ])
            .then(([pollData, voteData]) => {
                if (!pollData.success) { setError(pollData.message); return; }
                setPoll(pollData.poll);
                setAlreadyVoted(voteData.voted);
                setVotedOption(voteData.optionIndex);
                const closed = new Date(pollData.poll.endTime) <= new Date();
                setIsClosed(closed);
            })
            .catch(() => setError('Failed to load poll.'))
            .finally(() => setLoading(false));
    }, [id, voterKey]);

    // Countdown ticker
    useEffect(() => {
        if (!poll) return;
        const tick = () => {
            const ct = formatCountdown(poll.endTime);
            if (!ct) { setIsClosed(true); setCountdown('Poll Closed'); }
            else setCountdown(ct);
        };
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [poll]);

    const handleVote = useCallback(async () => {
        if (selected === null) return;
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/polls/${id}/vote`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ optionIndex: selected, voterKey }),
            });
            const data = await res.json();
            if (data.success) {
                setVoteMsg('✅ Your vote has been recorded! Thank you for participating.');
                setAlreadyVoted(true);
                setVotedOption(selected);
            } else {
                setVoteMsg(`❌ ${data.message}`);
            }
        } catch {
            setVoteMsg('❌ Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }, [selected, id, voterKey]);

    if (loading) return (
        <div className="page-wrapper">
            <Navbar />
            <div className="events-loading"><div className="events-spinner"></div><p>Loading poll…</p></div>
            <Footer />
        </div>
    );

    if (error) return (
        <div className="page-wrapper">
            <Navbar />
            <div className="page-content"><div className="events-error">{error}</div></div>
            <Footer />
        </div>
    );

    return (
        <div className="page-wrapper">
            <Navbar />
            <div className="page-hero">
                <h1>🗳️ {poll.title}</h1>
                {poll.description && <p>{poll.description}</p>}
            </div>
            <div className="page-content">
                {/* Status bar */}
                <div className={`poll-status-bar ${isClosed ? 'closed' : 'open'}`}>
                    {isClosed
                        ? '🔴 This poll is closed.'
                        : <>🟢 Poll is Live &nbsp;|&nbsp; ⏰ Time Remaining: <strong>{countdown}</strong></>
                    }
                </div>

                {/* Already-voted banner */}
                {alreadyVoted && (
                    <div className="poll-voted-banner">
                        ✅ You have already cast your vote
                        {votedOption !== null && poll?.options[votedOption]
                            ? ` for "${poll.options[votedOption].label}"`
                            : ''}.
                        Results will be announced after the poll closes.
                    </div>
                )}

                {/* Vote success/error message */}
                {voteMsg && (
                    <div className={`poll-msg ${voteMsg.startsWith('✅') ? 'success' : 'error'}`}>
                        {voteMsg}
                    </div>
                )}

                {/* Options */}
                <div className="poll-options-grid">
                    {poll.options.map((opt, idx) => (
                        <button
                            key={idx}
                            className={`poll-option-btn
                                ${selected === idx ? 'selected' : ''}
                                ${alreadyVoted && votedOption === idx ? 'voted' : ''}
                                ${(alreadyVoted || isClosed) ? 'disabled' : ''}
                            `}
                            onClick={() => !alreadyVoted && !isClosed && setSelected(idx)}
                            disabled={alreadyVoted || isClosed}
                            aria-pressed={selected === idx}
                        >
                            <span className="option-number">{idx + 1}</span>
                            <span className="option-label">{opt.label}</span>
                            {alreadyVoted && votedOption === idx && (
                                <span className="option-check">✔</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Submit */}
                {!alreadyVoted && !isClosed && (
                    <div className="poll-submit-area">
                        <button
                            className="poll-submit-btn"
                            onClick={handleVote}
                            disabled={selected === null || submitting}
                        >
                            {submitting ? 'Submitting…' : '🗳️ Submit Vote'}
                        </button>
                        {selected !== null && (
                            <p className="poll-selected-hint">
                                You selected: <strong>{poll.options[selected].label}</strong>
                            </p>
                        )}
                    </div>
                )}

                <button className="poll-back-btn" onClick={() => navigate('/events')}>
                    ← Back to Events
                </button>
            </div>
            <Footer />
        </div>
    );
}

export default PollVotePage;
